import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import { useRequest } from "@components/AppHook";
import { DndTable } from "@components/DND";
import { DevForm, FormFieldHelpText } from "@components/Forms/Fields";
import { Awesome, DevModal, Hint } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { url_reverse } from "@components/Utils";

const controlType = {
    radio: "radio",
    checkbox: "checkbox",
    button: "button",
    field: "field",
};

const subControlType = {
    image_button: "image_button",
    video_button: "video_button",
    text_field: "text_field",
    number_field: "number_field",
    date_time_field: "date_time_field",
    choice_field: "choice_field",
    multi_choice_field: "multi_choice_field",
    url_field: "url_field",
};

export default function SchemaConfigurationScreen() {
    const { t } = useTranslation();
    const params = useParams();
    const isEdit = Boolean(params.id);
    const apiUrl = isEdit
        ? url_reverse("api:schema_configure:edit", { schema_id: params.id })
        : url_reverse("api:schema_configure");
    const { apiData, setApiData } = useAppApi(apiUrl);
    const [name, setName] = useState("");
    const [selectedField, setSelectedField] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [searchChoices, setSearchChoices] = useState([]);
    const [searchSelected, setSearchSelected] = useState([]);
    const [configuration, setConfiguration] = useState({});
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const [apiSchemeItems, setApiSchemeItems] = useState({});
    const { api_get, api_post } = useRequest();

    useEffect(() => {
        setName(apiData?.schema?.name);
        const obj = {};
        apiData?.object_list?.forEach((el) => {
            obj[el.schema_item.id] = el.schema_item;
        });
        setConfiguration(obj);
    }, [apiData?.schema?.name]);

    useEffect(() => {
        if (showModal === true) {
            api_get(null, url_reverse("api:schema_item:list"), {
                then: (resp) => {
                    if (resp.status === "ok") {
                        setApiSchemeItems(resp);
                    }
                },
            });
        }
    }, [showModal]);

    useEffect(() => {
        if (apiSchemeItems && apiSchemeItems.object_list) {
            const choices = apiSchemeItems.object_list?.map((obj) => ({
                value: obj,
                label: obj.label,
            }));
            if (configuration) {
                const selectedKeys = Object.keys(configuration).map(Number);
                const filteredChoices = choices.filter((choice) => !selectedKeys.includes(choice.value.id));
                setSearchChoices(filteredChoices);
            } else {
                setSearchChoices(choices);
            }
        }
    }, [apiSchemeItems]);

    useEffect(() => {
        const updatedSelectedField = { ...selectedField };
        searchSelected.forEach((el) => {
            if (!selectedField[el.id]) {
                updatedSelectedField[el.id] = el;
            }
        });
        setSelectedField(updatedSelectedField);
    }, [searchSelected]);

    const handleSelectedField = (el) => {
        setSelectedField((prevSelectedField) => {
            if (prevSelectedField[el.id]) {
                const newSelectedField = { ...prevSelectedField };
                delete newSelectedField[el.id];
                return newSelectedField;
            }
            return { ...prevSelectedField, [el.id]: el };
        });
    };

    const handleSearchedField = (data) => {
        setSearchSelected(data);
    };
    const handleModalHide = () => {
        setSelectedField({});
        setSearchSelected([]);
        setShowModal(false);
    };

    const handleSubmitConfiguration = (ev) => {
        const ids = Object.keys(selectedField).map(Number);
        if (isEdit) {
            api_post(ev, apiUrl, {
                params: {
                    action: "add_schema_item",
                    name,
                    ids,
                },
                then: (resp) => {
                    if (resp.status === "ok") {
                        setApiData(resp);
                        setErrors({});
                        setShowModal(false);
                        setSelectedField({});
                        navigate(url_reverse("admin:event:schemes:configuration:edit", { scheme_id: params.id }));
                    } else if (resp.errors) {
                        setErrors(resp.errors);
                    }
                },
            });
        } else {
            api_post(ev, apiUrl, {
                params: { action: "set_schema_name", name },
                then: (resp) => {
                    if (resp.status === "ok") {
                        const schemaId = resp.schema_id;
                        setErrors({});
                        navigate(url_reverse("admin:event:schemes:configuration:edit", { scheme_id: schemaId }));
                    } else if (resp.errors) {
                        setErrors(resp.errors);
                    }
                },
            });
        }
    };

    const headerTools = (
        <Button variant="dark" size="sm" disabled={!name} onClick={() => setShowModal(true)}>
            {t("Добавить поле")}
        </Button>
    );

    const header = (
        <>
            <Link to={url_reverse("admin:event:schemes:list")}>{t("Управление шаблонами ивентов")}</Link>
            <span> – {t("Настройка шаблона ивента")} </span>
            {isEdit && <span>{apiData?.schema?.name}</span>}
        </>
    );

    const doAction = (ev) => {
        if (isEdit) {
            api_post(null, apiUrl, {
                params: { action: "set_schema_name", name: ev.target.value },
                then: (resp) => {
                    if (resp.status === "ok") {
                        setErrors({});
                        setApiData(resp);
                    } else if (resp.errors) {
                        setErrors(resp.errors);
                    }
                },
            });
        } else {
            api_post(null, apiUrl, {
                params: { action: "set_schema_name", name: ev.target.value },
                then: (resp) => {
                    if (resp.status === "ok") {
                        setErrors({});
                        navigate(url_reverse("admin:event:schemes:configuration:edit", { scheme_id: resp.schema_id }));
                    } else if (resp.errors) {
                        setErrors(resp.errors);
                    }
                },
            });
        }
    };

    const ParsedForm = ({ el }) => {
        return (
            <>
                <td style={{ width: "10%", fontWeight: "bold", verticalAlign: "top", transform: "translateY(10px)" }}>
                    {el.label}
                </td>
                <td style={{ width: "18%" }}>
                    {el.control_type === controlType.radio && (
                        <>
                            {Object.entries(el.radio_parameters).map(([key, value]) => (
                                <div key={key} style={{ display: "flex", alignItems: "center" }}>
                                    <Form.Check
                                        type="radio"
                                        label={
                                            <span>
                                                {value.radio} <small className="text-muted">({value.radio_note})</small>
                                            </span>
                                        }
                                        disabled={true}
                                    />
                                </div>
                            ))}
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <FormFieldHelpText help_text={el.help_text} />
                                <div style={{ marginLeft: "10px" }}>
                                    <Hint tooltip={el.tooltip} />
                                </div>
                            </div>
                        </>
                    )}
                    {el.control_type === controlType.checkbox && (
                        <>
                            {Object.entries(el.checkbox_parameters).map(([key, value]) => (
                                <div key={key} style={{ display: "flex", alignItems: "center" }}>
                                    <Form.Check
                                        type="checkbox"
                                        label={
                                            <span>
                                                {value.checkbox}{" "}
                                                <small className="text-muted">({value.checkbox_note})</small>
                                            </span>
                                        }
                                        disabled={true}
                                    />
                                </div>
                            ))}
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <FormFieldHelpText help_text={el.help_text} />
                                <div style={{ marginLeft: "10px" }}>
                                    <Hint tooltip={el.tooltip} />
                                </div>
                            </div>
                        </>
                    )}
                    {el.control_type === controlType.field && (
                        <>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                {el.sub_control_type === subControlType.choice_field && (
                                    <Form.Control as="select" disabled={true} style={{ width: "250px" }}>
                                        {el.field_parameters.choices.map((choice) => (
                                            <option key={choice.id}>{choice.label}</option>
                                        ))}
                                    </Form.Control>
                                )}
                                {el.sub_control_type === subControlType.multi_choice_field && (
                                    <DevForm.SelectMultiple choices={el.field_parameters.choices} disabled={true} />
                                )}
                                {el.sub_control_type === subControlType.number_field && (
                                    <Form.Control type="number" disabled={true} style={{ width: "250px" }} />
                                )}
                                {el.sub_control_type === subControlType.date_time_field && (
                                    <Form.Control type="date" disabled={true} style={{ width: "250px" }} />
                                )}
                                {el.sub_control_type === subControlType.text_field && (
                                    <Form.Control type="text" disabled={true} style={{ width: "250px" }} />
                                )}
                                {el.sub_control_type === subControlType.url_field && (
                                    <Form.Control type="text" disabled={true} style={{ width: "250px" }} />
                                )}
                                <div style={{ marginLeft: "10px" }}>
                                    <span style={{ float: "right" }}>
                                        <Hint tooltip={el.tooltip} />
                                    </span>
                                </div>
                            </div>
                            <FormFieldHelpText help_text={el.help_text} />
                        </>
                    )}
                    {el.control_type === controlType.button && (
                        <>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <Form.Control type="file" disabled={true} style={{ width: "250px" }} />
                                <div style={{ marginLeft: "10px" }}>
                                    <span style={{ float: "right" }}>
                                        <Hint tooltip={el.tooltip} />
                                    </span>
                                </div>
                            </div>
                            <FormFieldHelpText help_text={el.help_text} />
                        </>
                    )}
                </td>
                <td style={{ width: "25%", verticalAlign: "top", transform: "translateY(10px)" }}>
                    {el.is_developer_fill_allowed && (
                        <Form.Check disabled={true} inline label={t("Запросить материал у разработчика")} />
                    )}
                </td>
            </>
        );
    };

    const createModalForm = () => {
        return (
            <DevModal
                size="lg"
                show={showModal}
                onHide={handleModalHide}
                header={t("Добавить поле")}
                body={
                    <Card>
                        <Card.Body>
                            <DevForm.SelectMultiple choices={searchChoices} onChange={handleSearchedField} />
                            <Table responsive className="dmg-table mt-2">
                                <tbody>
                                    {apiSchemeItems?.object_list?.map((el) => {
                                        const isDisabled = Object.prototype.hasOwnProperty.call(configuration, el.id);
                                        return (
                                            <tr key={el.id} style={{ opacity: isDisabled ? 0.5 : 1 }}>
                                                <td
                                                    style={{
                                                        width: "5%",
                                                        verticalAlign: "top",
                                                        transform: "translateY(10px)",
                                                    }}
                                                >
                                                    <Form.Check
                                                        data-id={el.id}
                                                        checked={Object.prototype.hasOwnProperty.call(
                                                            selectedField,
                                                            el.id
                                                        )}
                                                        onChange={() => handleSelectedField(el)}
                                                        disabled={isDisabled}
                                                    />
                                                </td>
                                                <ParsedForm el={el} />
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                }
                footer={
                    <DevForm.Button variant="primary" name="save" onClick={handleSubmitConfiguration}>
                        {t("Сохранить")}
                    </DevForm.Button>
                }
            />
        );
    };
    return (
        <DefaultLayout>
            <Card>
                {errors?.items_not_found && <Alert variant="danger">{errors.items_not_found}</Alert>}
                {errors?.schema_not_found && <Alert variant="danger">{errors.schema_not_found}</Alert>}
                <Card.Header>
                    {header}
                    <div className="dmg-card-header-tools">{headerTools}</div>
                </Card.Header>
                <Card.Body>
                    {createModalForm()}
                    <div style={{ marginBottom: "20px", display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex", alignItems: "center", fontWeight: "bold" }}>
                            <label>
                                {t("Название")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </label>
                            <DevForm.TextInput
                                type="text"
                                id="name"
                                name="name"
                                value={name || ""}
                                onChange={(ev) => setName(ev.target.value)}
                                onBlur={doAction}
                                style={{ width: "500px", marginLeft: "10px" }}
                            />
                        </div>
                        {errors?.name && (
                            <div className="error" style={{ color: "red", marginLeft: "100px", marginTop: "5px" }}>
                                {errors.name}
                            </div>
                        )}
                    </div>
                    <SchemaItemSortable apiData={apiData} setApiData={setApiData} apiUrl={apiUrl} />
                </Card.Body>
            </Card>
        </DefaultLayout>
    );
}

function SchemaItemSortable({ apiData, setApiData, apiUrl }) {
    const { t } = useTranslation();
    const { api_post } = useRequest();

    const handleFieldRequired = (id) => {
        api_post(null, apiUrl, {
            params: { action: "set_field_required", id },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    const handleDeleteSchemeItem = (id) => {
        api_post(null, apiUrl, {
            params: { action: "delete_scheme_scheme_item", id },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };
    const ParsedForm = ({ el, id, required }) => (
        <>
            <td style={{ width: "10%", fontWeight: "bold" }}>{el.label}</td>
            <td style={{ width: "10%" }}>
                {el.control_type === controlType.radio && (
                    <>
                        {Object.entries(el.radio_parameters).map(([key, value]) => (
                            <React.Fragment key={key}>
                                <div key={key} style={{ display: "flex", alignItems: "center" }}>
                                    <Form.Check
                                        key={key}
                                        type="radio"
                                        label={
                                            <span>
                                                {value.radio} <small className="text-muted">({value.radio_note})</small>
                                            </span>
                                        }
                                        disabled={true}
                                    />
                                </div>
                            </React.Fragment>
                        ))}
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <FormFieldHelpText help_text={el.help_text} />
                            <div style={{ marginLeft: "10px" }}>
                                <Hint tooltip={el.tooltip} />
                            </div>
                        </div>
                    </>
                )}
                {el.control_type === controlType.checkbox && (
                    <>
                        {Object.entries(el.checkbox_parameters).map(([key, value]) => (
                            <div key={key} style={{ display: "flex", alignItems: "center" }}>
                                <Form.Check
                                    type="checkbox"
                                    label={
                                        <span>
                                            {value.checkbox}{" "}
                                            <small className="text-muted">({value.checkbox_note})</small>
                                        </span>
                                    }
                                    disabled={true}
                                />
                            </div>
                        ))}
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <FormFieldHelpText help_text={el.help_text} />
                            <div style={{ marginLeft: "10px" }}>
                                <Hint tooltip={el.tooltip} />
                            </div>
                        </div>
                    </>
                )}
                {el.control_type === controlType.field && (
                    <>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            {el.sub_control_type === subControlType.choice_field && (
                                <Form.Control as="select" disabled={true} style={{ width: "250px" }}>
                                    {el.field_parameters.choices.map((choice) => (
                                        <option key={choice.id}>{choice.label}</option>
                                    ))}
                                </Form.Control>
                            )}
                            {el.sub_control_type === subControlType.multi_choice_field && (
                                <DevForm.SelectMultiple choices={el.field_parameters.choices} disabled={true} />
                            )}
                            {el.sub_control_type === subControlType.number_field && (
                                <Form.Control type="number" disabled={true} style={{ width: "250px" }} />
                            )}
                            {el.sub_control_type === subControlType.date_time_field && (
                                <Form.Control type="date" disabled={true} style={{ width: "250px" }} />
                            )}
                            {el.sub_control_type === subControlType.text_field && (
                                <Form.Control type="text" disabled={true} style={{ width: "250px" }} />
                            )}
                            {el.sub_control_type === subControlType.url_field && (
                                <Form.Control type="text" disabled={true} style={{ width: "250px" }} />
                            )}
                            <div style={{ marginLeft: "10px" }}>
                                <span style={{ float: "right" }}>
                                    <Hint tooltip={el.tooltip} />
                                </span>
                            </div>
                        </div>
                        <FormFieldHelpText help_text={el.help_text} />
                    </>
                )}
                {el.control_type === controlType.button && (
                    <>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <Form.Control type="file" disabled={true} style={{ width: "250px" }} />
                            <div style={{ marginLeft: "10px" }}>
                                <span style={{ float: "right" }}>
                                    <Hint tooltip={el.tooltip} />
                                </span>
                            </div>
                        </div>
                        <FormFieldHelpText help_text={el.help_text} />
                    </>
                )}
            </td>
            <td style={{ width: "5%" }}>
                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteSchemeItem(id)}>
                    <Awesome type="trash-o" />
                </Button>
            </td>
            <td style={{ width: "15%" }}>
                {el.is_developer_fill_allowed && (
                    <DevForm.Checkbox disabled={true} inline label={t("Запросить материал у разработчика")} />
                )}
            </td>
            <td style={{ width: "15%" }}>
                <DevForm.Checkbox
                    checked={required}
                    onChange={() => handleFieldRequired(id)}
                    inline
                    label={t("Обязательное поле.")}
                />
            </td>
        </>
    );

    return (
        <DndTable
            apiData={apiData}
            setApiData={setApiData}
            apiUrl={apiUrl}
            tds={(el) => (
                <>
                    <td style={{ width: "5%" }} />
                    <ParsedForm el={el.schema_item} id={el.id} required={el.required} />
                </>
            )}
        />
    );
}
